import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import styles from './News.module.scss';
import { PaginationRoutes } from '@/components/PaginationRoutes';
import { NewsType, SettingsType } from '@/types';
import { Card, CardSize, CardType, TagPosition } from '@/components/Card';
import { PageTitle } from '@/components/PageTitle';
import { Button } from '@/components/Button';
import { Palettes, Variants } from '@/types';
import { fetchData } from '@/services/fetchData';
import { Layout } from '@/layout';
import { SubscriptionForm } from '@/components/SubscriptionForm';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';

const News = ({
  content,
  menu,
  translations,
  locale,
  common,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { isTablet } = useAdaptiveWidth();
  const numberOfShown = isTablet ? 4 : 8;
  const maxPages = content.pagination.countPages;
  const router = useRouter();
  const [pagesCount, setPagesCount] = useState(1);
  const [items, setItems] = useState(content.news);

  const getMore = async () => {
    if (pagesCount === maxPages) return;
    const newItems = await fetchData({
      url: `/content/news/list?page=${pagesCount + 1}&count=${numberOfShown}`,
    });

    setItems([...items, ...newItems.list]);
    setPagesCount((prev) => prev + 1);
  };

  return (
    <Layout data={menu} meta={content.meta}>
      <main className={styles.page}>
        <PaginationRoutes
          routes={[
            {
              link: '/news',
              text: common.newsLink,
            },
          ]}
        />
        <PageTitle text={translations.newsTitle} />
        <div className={styles.objects}>
          <div className={styles.objectsContent}>
            {items.map((item: NewsType, index: number) => (
              <Card
                key={item.id + index}
                picture={item.picture}
                name={item.name}
                code={item.code}
                cardType={CardType.Panel}
                size={CardSize.SM}
                dateFrom={item.date}
                link={`/news/${item.code}`}
                tagPosition={TagPosition.Top}
                noType
                twoRowTitle
                pageMissing={item.pageMissing}
                withYear
                fullWidth
              />
            ))}
          </div>
          {pagesCount < maxPages && (
            <Button
              text={common.showMoreButton}
              onClick={getMore}
              variant={Variants.Outline}
              palette={Palettes.Secondary}
              className={styles.more}
            />
          )}
          <Button
            text={translations.buttonArchive}
            onClick={() => router.push('/news-archive')}
            icon="/svg/arrow.svg"
            className={styles.archiveButton}
            variant={Variants.Ghost}
          />
        </div>
        <SubscriptionForm translations={common} locale={locale} />
      </main>
    </Layout>
  );
};

export async function getServerSideProps({
  locale,
}: GetServerSidePropsContext) {
  const content = await fetchData({
    url: `/news/view`,
    locale,
  });

  const menu: SettingsType = await fetchData({
    url: `/settings/view`,
    locale,
  });

  const isError = !content || content.code !== 200;
  if (isError) return { notFound: true };

  return {
    props: {
      content,
      menu,
      translations: menu.translations['/news'],
      locale,
      common: menu.translations.common,
    },
  };
}

export default News;
